// BUILD-046 Owner-requested polish (visual edits ledger, session edit 4.5; finish-b): the clickable column headings of the task table (Task, Assigned to, Priority,
// Task due date, Status - each where the table has that column).
//
// The page offers its ONE sort state through this context (mode in force, the modes the view offers, and the setter the "Sort: <selection>" dropdown also uses),
// so the dropdown and the headings read and write the same state. What a heading means (which existing mode a click selects, the arrow, aria-sort and the
// accessible name) is sortHeadingModel.ts's; this file draws it. A table outside the provider (Assigned by Me, which has no sort) shows plain headings.
import { createContext, useContext } from 'react';
import type { PageSortMode } from './pageSortModel';
import type { SortHeading } from './sortHeadingModel';

export interface SortHeadingsValue {
  /** The sort mode in force (the one the dropdown shows). */
  mode: PageSortMode;
  /** The modes this view offers. */
  available: readonly PageSortMode[];
  /** Selects a sort mode (the page's own setter). */
  select: (mode: PageSortMode) => void;
}

export const SortHeadingsContext = createContext<SortHeadingsValue | null>(null);

export function useSortHeadings(): SortHeadingsValue | null {
  return useContext(SortHeadingsContext);
}

function SortArrow({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0" data-testid="sort-arrow" data-direction={direction}>
      {direction === 'up' ? <path d="M6 2.5l4 6.5H2z" fill="currentColor" /> : <path d="M6 9.5L2 3h8z" fill="currentColor" />}
    </svg>
  );
}

/**
 * The heading as a button. The negative margin and the matching padding keep the heading TEXT exactly where a plain heading's text would be, so the column
 * still lines up with the cells below it. The active heading is darker and carries the arrow; the focus ring is visible.
 */
export function SortHeadingButton({ heading, onSelect }: { heading: SortHeading; onSelect: (mode: PageSortMode) => void }) {
  return (
    <button
      type="button"
      aria-label={heading.ariaLabel}
      title={heading.ariaLabel}
      data-testid={`sort-heading-${heading.column}`}
      data-active={heading.active ? 'true' : 'false'}
      onClick={() => {
        if (heading.next !== null) onSelect(heading.next);
      }}
      className={`-mx-1 -my-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 text-left font-semibold uppercase tracking-wide hover:bg-gray-200/70 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] ${heading.active ? 'text-gray-900' : 'text-gray-600'}`}
    >
      <span>
        {heading.lines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </span>
      {heading.arrow !== null && <SortArrow direction={heading.arrow} />}
    </button>
  );
}
